import React, { useEffect, useState, useRef } from 'react';
import { Container, Form, Button, Alert, Spinner, Row, Col } from 'react-bootstrap';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { obtenerProductoPorId, actualizarProducto, crearProducto } from '../../services/productoService';
import { subirImagen, eliminarImagen } from '../../services/imagenService';
import { ROUTES, CATEGORIAS } from '../../constants';
import { toNumber } from '../../utils/formatters';
import './Admin.css';

function AdminEditarProducto() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const initialImagenRef = useRef({ imagenURL: '', imagenGestionadaPorApi: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [archivoImagen, setArchivoImagen] = useState(null);
  const [nombreArchivoSubida, setNombreArchivoSubida] = useState('');
  const [imagenGestionadaPorApi, setImagenGestionadaPorApi] = useState(false);

  const [formData, setFormData] = useState({
    modelo: '',
    material: '',
    color: '',
    cataccesorios: '',
    precio: '',
    preciomenor: '',
    stock: '',
    categoria: '',
    imagenURL: '',
    destacado: false
  });

  const esNuevo = location.pathname.includes('/nuevo') || !id;

  useEffect(() => {
    if (esNuevo) {
      initialImagenRef.current = { imagenURL: '', imagenGestionadaPorApi: false };
      setImagenGestionadaPorApi(false);
      setArchivoImagen(null);
      setNombreArchivoSubida('');
      setLoading(false);
      return;
    }

    const fetchProducto = async () => {
      if (!id) {
        setError('ID de producto no válido');
        setLoading(false);
        return;
      }

      try {
        const producto = await obtenerProductoPorId(id);

        if (!producto) {
          setError('Producto no encontrado');
          setLoading(false);
          return;
        }

        const imagenURL = producto.imagenURL || '';
        const gestionada = producto.imagenGestionadaPorApi === true;
        initialImagenRef.current = { imagenURL, imagenGestionadaPorApi: gestionada };
        setImagenGestionadaPorApi(gestionada);
        setArchivoImagen(null);
        setNombreArchivoSubida('');
        setFormData({
          modelo: producto.modelo || '',
          material: producto.material || '',
          color: producto.color || '',
          cataccesorios: producto.cataccesorios || '',
          precio: producto.precio != null ? String(producto.precio) : '',
          preciomenor: producto.preciomenor != null ? String(producto.preciomenor) : '',
          stock: producto.stock != null ? String(producto.stock) : '',
          categoria: producto.categoria || '',
          imagenURL: imagenURL,
          destacado: producto.destacado === true
        });
      } catch (e) {
        console.error('Error al cargar producto:', e);
        setError(e.message || 'Error al cargar el producto');
      } finally {
        setLoading(false);
      }
    };

    fetchProducto();
  }, [id, esNuevo]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleArchivoImagenChange = (e) => {
    const file = e.target.files && e.target.files[0];
    setArchivoImagen(file || null);
    if (file) {
      setNombreArchivoSubida((prev) => (prev && prev.trim() !== '' ? prev : file.name || ''));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);

    let imagenURLFinal = (formData.imagenURL || '').trim();
    let nombreSubidoEstaSesion = null;

    try {
      if (archivoImagen) {
        try {
          const nombreOpt = nombreArchivoSubida.trim() || null;
          nombreSubidoEstaSesion = await subirImagen(archivoImagen, nombreOpt);
          imagenURLFinal = nombreSubidoEstaSesion;
          setFormData((prev) => ({ ...prev, imagenURL: nombreSubidoEstaSesion }));
        } catch (uploadErr) {
          console.error('Error al subir imagen:', uploadErr);
          setError(uploadErr.message || 'No se pudo subir la imagen. Revisá formato PNG, tamaño y nombre de archivo.');
          setSaving(false);
          return;
        }
      }

      let imagenGestionadaFlag = imagenGestionadaPorApi;
      if (archivoImagen) {
        imagenGestionadaFlag = true;
      } else if (imagenURLFinal !== initialImagenRef.current.imagenURL) {
        imagenGestionadaFlag = false;
      }

      const updateData = {
        modelo: formData.modelo.trim() || null,
        material: formData.material.trim() || null,
        color: formData.color.trim() || null,
        cataccesorios: formData.cataccesorios.trim() || null,
        precio: formData.precio ? toNumber(formData.precio) : null,
        preciomenor: formData.preciomenor ? toNumber(formData.preciomenor) : null,
        stock: formData.stock ? toNumber(formData.stock) : null,
        categoria: formData.categoria.trim() || null,
        imagenURL: imagenURLFinal || null,
        imagenGestionadaPorApi: imagenGestionadaFlag,
        destacado: formData.destacado
      };

      try {
        if (esNuevo) {
          await crearProducto(updateData);
        } else {
          await actualizarProducto(id, updateData);
        }
      } catch (fsErr) {
        console.error('Error al guardar en Firestore:', fsErr);
        if (nombreSubidoEstaSesion) {
          try {
            await eliminarImagen(nombreSubidoEstaSesion);
          } catch (rollbackErr) {
            console.warn('No se pudo revertir la imagen subida:', rollbackErr);
          }
        }
        setError(fsErr.message || 'La imagen se subió pero no se pudo guardar el producto. Intentá de nuevo.');
        setSaving(false);
        return;
      }

      setFormData((prev) => ({
        ...prev,
        imagenURL: imagenURLFinal,
      }));
      setImagenGestionadaPorApi(imagenGestionadaFlag);
      initialImagenRef.current = {
        imagenURL: imagenURLFinal,
        imagenGestionadaPorApi: imagenGestionadaFlag,
      };
      setArchivoImagen(null);
      setNombreArchivoSubida('');

      setSuccess(true);
      setSaving(false);

      setTimeout(() => {
        navigate(ROUTES.ADMIN_PRODUCTOS);
      }, 1500);
    } catch (e) {
      console.error('Error al procesar producto:', e);
      setError(e.message || (esNuevo ? 'Error al crear el producto. Por favor, intenta nuevamente.' : 'Error al actualizar el producto. Por favor, intenta nuevamente.'));
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container className="py-4">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spinner animation="border" />
        </div>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="admin-section-title mb-0">{esNuevo ? 'Nuevo Producto' : 'Editar Producto'}</h5>
        <Button variant="primary" size="sm" onClick={() => navigate(ROUTES.ADMIN_PRODUCTOS)}>
          Volver al listado
        </Button>
      </div>

      {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
      {success && (
        <Alert variant="success" className="mt-3">
          {esNuevo ? 'Producto creado exitosamente. Redirigiendo...' : 'Producto actualizado exitosamente. Redirigiendo...'}
        </Alert>
      )}

      <div className="admin-form-container">
        <Form onSubmit={handleSubmit}>
        <Row>
          <Col md={6}>
            <Form.Group className="admin-form-group">
              <Form.Label className="admin-form-label">Categoría</Form.Label>
              <Form.Select
                name="categoria"
                value={formData.categoria}
                onChange={handleChange}
                className="admin-form-select"
              >
                <option value="">Seleccionar categoría</option>
                <option value={CATEGORIAS.FUNDAS}>Fundas</option>
                <option value={CATEGORIAS.ACCESORIOS}>Accesorios</option>
                <option value={CATEGORIAS.VIDRIOS}>Vidrios</option>
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group className="admin-form-group">
              <Form.Label className="admin-form-label">Modelo</Form.Label>
              <Form.Control
                type="text"
                name="modelo"
                value={formData.modelo}
                onChange={handleChange}
                placeholder="Modelo"
                className="admin-form-control"
              />
            </Form.Group>
          </Col>

          {formData.categoria === CATEGORIAS.FUNDAS && (
            <>
              <Col md={4}>
                <Form.Group className="admin-form-group">
                  <Form.Label className="admin-form-label">Material</Form.Label>
                  <Form.Control
                    type="text"
                    name="material"
                    value={formData.material}
                    onChange={handleChange}
                    placeholder="Material"
                    className="admin-form-control"
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="admin-form-group">
                  <Form.Label className="admin-form-label">Color</Form.Label>
                  <Form.Control
                    type="text"
                    name="color"
                    value={formData.color}
                    onChange={handleChange}
                    placeholder="Color"
                    className="admin-form-control"
                  />
                </Form.Group>
              </Col>
            </>
          )}

          {formData.categoria === CATEGORIAS.VIDRIOS && (
            <Col md={4}>
              <Form.Group className="admin-form-group">
                <Form.Label className="admin-form-label">Modelo de vidrio</Form.Label>
                <Form.Control
                  type="text"
                  name="material"
                  value={formData.material}
                  onChange={handleChange}
                  placeholder="Modelo de vidrio"
                  className="admin-form-control"
                />
              </Form.Group>
            </Col>
          )}

          {formData.categoria === CATEGORIAS.ACCESORIOS && (
            <Col md={4}>
              <Form.Group className="admin-form-group">
                <Form.Label className="admin-form-label">Categoría de Accesorio</Form.Label>
                <Form.Control
                  type="text"
                  name="cataccesorios"
                  value={formData.cataccesorios}
                  onChange={handleChange}
                  placeholder="Ej: Cargadores, Auriculares, etc."
                  className="admin-form-control"
                />
              </Form.Group>
            </Col>
          )}

          <Col md={4}>
            <Form.Group className="admin-form-group">
              <Form.Label className="admin-form-label">Precio (Mayorista)</Form.Label>
              <Form.Control
                type="number"
                name="precio"
                value={formData.precio}
                onChange={handleChange}
                placeholder="0"
                step="0.01"
                min="0"
                className="admin-form-control"
              />
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group className="admin-form-group">
              <Form.Label className="admin-form-label">Precio (Minorista)</Form.Label>
              <Form.Control
                type="number"
                name="preciomenor"
                value={formData.preciomenor}
                onChange={handleChange}
                placeholder="0"
                step="0.01"
                min="0"
                className="admin-form-control"
              />
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group className="admin-form-group">
              <Form.Label className="admin-form-label">Stock</Form.Label>
              <Form.Control
                type="number"
                name="stock"
                value={formData.stock}
                onChange={handleChange}
                placeholder="0"
                step="1"
                min="0"
                className="admin-form-control"
              />
            </Form.Group>
          </Col>
        </Row>

        <Row className="mt-3">
          <Col md={12}>
            <Form.Group className="admin-form-group">
              <Form.Label className="admin-form-label">Imagen del producto</Form.Label>
              <Form.Control
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={handleArchivoImagenChange}
                className="admin-form-control"
                disabled={saving}
              />
              <Form.Text className="text-muted">
                PNG, JPEG o WebP (en el servidor se guarda como PNG). Primero se sube el archivo y después se guarda el producto en Firestore.
              </Form.Text>
            </Form.Group>
          </Col>
          <Col md={12} className="mt-2">
            <Form.Group className="admin-form-group">
              <Form.Label className="admin-form-label">Nombre de archivo al subir (.png)</Form.Label>
              <Form.Control
                type="text"
                value={nombreArchivoSubida}
                onChange={(ev) => setNombreArchivoSubida(ev.target.value)}
                placeholder="ej: mi-producto.png (solo letras, números, . _ -)"
                className="admin-form-control"
                disabled={saving || !archivoImagen}
              />
            </Form.Group>
          </Col>
          <Col md={12} className="mt-2">
            <Form.Group className="admin-form-group">
              <Form.Label className="admin-form-label">Nombre / URL de imagen en Firestore</Form.Label>
              <Form.Control
                type="text"
                name="imagenURL"
                value={formData.imagenURL}
                onChange={handleChange}
                placeholder="nombre.png (se actualiza al subir) o URL externa"
                className="admin-form-control"
                disabled={saving}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row className="mt-3">
          <Col md={12}>
            <div className="border rounded p-3 admin-checkbox-container" style={{ backgroundColor: '#f8f9fa' }}>
              <Form.Group className="mb-0 d-flex align-items-center justify-content-between w-100">
                <Form.Check
                  type="checkbox"
                  name="destacado"
                  label={<strong style={{ fontSize: '1.1rem' }}>Producto destacado</strong>}
                  checked={formData.destacado}
                  onChange={handleChange}
                  style={{ fontSize: '1.2rem' }}
                  className="admin-checkbox-destacado mb-0"
                />
                <Form.Text className="text-muted mb-0 ms-3">
                  Los productos destacados aparecen en la sección principal de la página de inicio
                </Form.Text>
              </Form.Group>
            </div>
          </Col>
        </Row>

        <div className="admin-form-actions d-flex gap-2">
          <Button
            variant="primary"
            type="submit"
            disabled={saving}
          >
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {archivoImagen ? 'Subiendo imagen y guardando...' : esNuevo ? 'Creando producto...' : 'Guardando...'}
              </>
            ) : (
              esNuevo ? 'Crear Producto' : 'Guardar Cambios'
            )}
          </Button>
          <Button
            variant="outline-secondary"
            type="button"
            onClick={() => navigate(ROUTES.ADMIN_PRODUCTOS)}
            disabled={saving}
          >
            Cancelar
          </Button>
        </div>
      </Form>
      </div>
    </Container>
  );
}

export default AdminEditarProducto;
